import React, { useEffect, useState, useRef } from 'react';
import api from '../../utils/api';
import { Spinner, EmptyState, useToast, SearchableSelect, InlineSpinner, TableSkeletonRows } from '../../components/UI';
import { SectionIcon } from '../../components/TrainerUI';
import { fmtPct } from '../../utils/format';

export default function TrainerComponentMarksPage() {
  const toast = useToast();
  const [options, setOptions] = useState({ modules: [], intakes: [], intakeModules: [], permitted: [] });
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [intakeCode, setIntakeCode] = useState('');
  const [moduleCode, setModuleCode] = useState('');
  const [examType, setExamType] = useState('');
  const [components, setComponents] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [saving, setSaving] = useState(false);
  const [overLimit, setOverLimit] = useState({}); // `${student_no}:${component_id}` -> true
  const inputRefs = useRef({});

  useEffect(() => {
    api.get('/trainer/component-marks/options').then(r => setOptions(r.data.data || { modules: [], intakes: [], intakeModules: [], permitted: [] }))
      .catch(() => toast('Failed to load intakes/modules', 'error'))
      .finally(() => setOptionsLoaded(true));
  }, []);

  // Two different "nothing to show" reasons, kept distinct: admin never
  // granted this trainer anything at all (permitted is empty) vs. admin did
  // grant something but Component Master hasn't been set up for it yet
  // (permitted has rows, intakeModules — which also requires a component
  // setup — doesn't).
  const hasAnyPermission = options.permitted.length > 0;
  const hasAnySelectable = options.intakeModules.length > 0;

  // Intakes the trainer's actually been granted at least one *usable*
  // module in — narrows the Intake picker itself instead of letting them
  // pick an intake that's guaranteed to show an empty Module dropdown.
  const grantedIntakeCodes = [...new Set(options.intakeModules.map(im => im.intake_code))];

  const modulesForIntake = intakeCode
    ? [...new Map(options.intakeModules.filter(im => im.intake_code === intakeCode).map(im => [im.module_code, im])).values()]
    : [];
  const examTypesForModule = (intakeCode && moduleCode)
    ? options.intakeModules.filter(im => im.intake_code === intakeCode && im.module_code === moduleCode).map(im => im.exam_type)
    : [];
  const moduleName = code => options.modules.find(m => m.module_code === code)?.module_name || code;

  const handleIntakeChange = code => { setIntakeCode(code); setModuleCode(''); setExamType(''); setComponents([]); setRoster([]); };

  // Round to at most 1 decimal place, then drop it entirely if it's a whole
  // number — 20.55 -> "20.6", 20.5 -> "20.5", 20.00 (as returned by the DB's
  // DECIMAL column) -> "20" — so the input shows what was actually entered,
  // not MySQL's fixed 2-decimal formatting.
  const fmtScore = v => {
    const rounded = Math.round(Number(v) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const loadRoster = (intake_code, module_code, exam_type) => {
    setLoadingRoster(true);
    api.get('/trainer/component-marks', { params: { intake_code, module_code, exam_type } })
      .then(r => {
        setComponents(r.data.data.components);
        setRoster(r.data.data.roster.map(row => ({
          ...row,
          scores: Object.fromEntries(Object.entries(row.scores).map(([id, v]) => [id, v === '' || v == null ? v : fmtScore(v)])),
        })));
        setOverLimit({});
      })
      .catch(() => toast('Failed to load marking sheet', 'error'))
      .finally(() => setLoadingRoster(false));
  };

  const handleModuleChange = code => {
    setModuleCode(code);
    setComponents([]); setRoster([]);
    const types = intakeCode ? options.intakeModules.filter(im => im.intake_code === intakeCode && im.module_code === code).map(im => im.exam_type) : [];
    // Auto-pick when there's only one exam type granted for this module — no point making the trainer choose.
    const nextType = types.length === 1 ? types[0] : '';
    setExamType(nextType);
    if (intakeCode && code && nextType) loadRoster(intakeCode, code, nextType);
  };

  const handleExamTypeChange = type => {
    setExamType(type);
    if (intakeCode && moduleCode && type) loadRoster(intakeCode, moduleCode, type);
  };

  const leafComponents = components.filter(c => !components.some(o => o.parent_id === c.id));
  const topLevel = components.filter(c => !c.parent_id);

  // Same flattening the header uses (topLevel item, or its children if it's a
  // group) so header columns and body cells always line up, plus a groupStart
  // flag marking where a vertical divider belongs: before the very first
  // column, and before any composite group's first child (e.g. Exam's
  // Section A) — not between two plain single-value columns like
  // Assignment/Project.
  const orderedLeaves = [];
  let leafIdx = 0;
  topLevel.forEach(t => {
    const children = components.filter(c => c.parent_id === t.id);
    const items = children.length ? children : [t];
    items.forEach((c, ci) => {
      orderedLeaves.push({ ...c, groupStart: leafIdx === 0 || (children.length > 0 && ci === 0) });
      leafIdx++;
    });
  });

  const cellKey = (student_no, component_id) => `${student_no}:${component_id}`;

  const setScore = (student_no, component_id, value) => {
    const max = leafComponents.find(l => l.id === component_id)?.percentage ?? 0;
    const key = cellKey(student_no, component_id);
    const num = value === '' ? null : Number(value);
    const exceeds = num !== null && !Number.isNaN(num) && num > Number(max);

    setOverLimit(prev => ({ ...prev, [key]: exceeds }));
    setRoster(rows => rows.map(r => r.student_no === student_no
      ? { ...r, scores: { ...r.scores, [component_id]: value } }
      : r));

    if (exceeds) {
      // Focus stays on the field itself; the red ring is driven by overLimit[key] below.
      requestAnimationFrame(() => inputRefs.current[key]?.focus());
    }
  };

  const anyOverLimit = Object.values(overLimit).some(Boolean);

  // A plain top-level item (Assignment, Project) needs a mark; a group
  // (Exam) only needs at least one of its sections filled — students often
  // choose which sections to answer, so requiring every section would never
  // reach "done".
  const topRequirementMet = (r, t) => {
    const children = components.filter(c => c.parent_id === t.id);
    if (children.length) return children.some(c => r.scores[c.id] !== '' && r.scores[c.id] != null);
    return r.scores[t.id] !== '' && r.scores[t.id] != null;
  };
  const filledCount = r => topLevel.filter(t => topRequirementMet(r, t)).length;
  const studentsDone = roster.filter(r => filledCount(r) === topLevel.length).length;
  const initials = name => name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  // Top-level percentages always sum to 100 (enforced at setup), so the max
  // is always 100 — some exams let students choose which sections to
  // answer (e.g. 2 of 3), so the sum of every leaf max isn't a real cap.
  const totalMax = topLevel.reduce((sum, t) => sum + Number(t.percentage || 0), 0);
  // Sum whatever scores were actually entered — unanswered/optional sections
  // just contribute 0, they don't get weighted against sections the student
  // wasn't required to attempt.
  const rowTotal = r => leafComponents.reduce((sum, l) => {
    const v = r.scores[l.id];
    return sum + (v === '' || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
  }, 0);
  const rowOverTotal = r => rowTotal(r) > totalMax;
  const anyOverTotal = roster.some(rowOverTotal);

  const handleSave = async () => {
    if (anyOverLimit || anyOverTotal) { toast('Fix marks that exceed their maximum before saving', 'error'); return; }
    const scores = [];
    for (const r of roster) {
      if (r.status === 'locked' || r.status === 'released') continue; // locked — nothing to resend
      for (const l of leafComponents) {
        scores.push({ student_no: r.student_no, component_id: l.id, score: r.scores[l.id] });
      }
    }
    if (!scores.length) return;
    setSaving(true);
    try {
      await api.put('/trainer/component-marks', { intake_code: intakeCode, module_code: moduleCode, exam_type: examType, scores });
      toast('Marks saved', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save marks', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="trn-topbar">
        <div className="trn-topbar-title">Marking</div>
        <div className="trn-topbar-sub">Enter student marks per component</div>
      </div>
      <div className="trn-page">
        <div className="trn-card">
          <div className="trn-card-head"><h3 className="d-flex align-items-center"><SectionIcon icon="bi-diagram-3" color="var(--sms-stat-cyan)" />Component Marking Sheet</h3></div>
          <div className="trn-card-body">
            {!optionsLoaded ? <Spinner minHeight="240px" /> : !hasAnyPermission ? (
              <EmptyState icon="bi-lock" message="You haven't been granted marking access for any module yet — check with admin." />
            ) : !hasAnySelectable ? (
              <EmptyState icon="bi-hourglass-split" message="You've been granted marking permission, but the marking scheme (Component Master) hasn't been set up for it yet — check with admin." />
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-12 col-sm-6">
                    <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake</label>
                    <SearchableSelect value={intakeCode} placeholder="Select intake…" onChange={e => handleIntakeChange(e.target.value)}
                      options={options.intakes.filter(i => grantedIntakeCodes.includes(i.intake_code)).map(i => ({ value: i.intake_code, label: i.intake_code }))} />
                  </div>
                  <div className="col-12 col-sm-6 col-lg-4">
                    <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module</label>
                    <SearchableSelect value={moduleCode} disabled={!intakeCode} placeholder={intakeCode ? 'Select module…' : 'Select an intake first'}
                      onChange={e => handleModuleChange(e.target.value)}
                      options={modulesForIntake.map(im => ({ value: im.module_code, label: moduleName(im.module_code) }))} />
                  </div>
                  {moduleCode && examTypesForModule.length > 1 && (
                    <div className="col-12 col-sm-6 col-lg-2">
                      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Exam Type</label>
                      <SearchableSelect value={examType} placeholder="Select…" onChange={e => handleExamTypeChange(e.target.value)}
                        options={examTypesForModule.map(t => ({ value: t, label: t === 'resit' ? 'Resit' : 'Main' }))} />
                    </div>
                  )}
                </div>

                {!intakeCode || !moduleCode ? (
              <EmptyState icon="bi-diagram-3" message="Select an intake and module to load the marking sheet" />
            ) : !examType ? (
              <EmptyState icon="bi-flag" message="Select an exam type to load the marking sheet" />
            ) : !loadingRoster && !leafComponents.length ? (
              <EmptyState icon="bi-diagram-3" message="No component setup found for this intake and module" />
            ) : !loadingRoster && !roster.length ? (
              <EmptyState icon="bi-people" message="No active students in this batch" />
            ) : (
              <>
                <div className="cm-progress">
                  <span><b>{studentsDone}</b> of {roster.length} students fully marked</span>
                  <div className="cm-progress-bar"><span style={{ width: `${roster.length ? Math.round(studentsDone / roster.length * 100) : 0}%` }} /></div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="trn-table cm-table">
                    <thead>
                      <tr>
                        <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Student</th>
                        {topLevel.map(t => {
                          const children = components.filter(c => c.parent_id === t.id);
                          const groupStart = orderedLeaves.find(l => l.id === (children.length ? children[0].id : t.id))?.groupStart;
                          return children.length
                            ? <th key={t.id} colSpan={children.length} className={groupStart ? 'cm-group-start' : ''} style={{ textAlign: 'center' }}>{t.name}</th>
                            : <th key={t.id} rowSpan={2} className={groupStart ? 'cm-group-start' : ''} style={{ verticalAlign: 'bottom', textAlign: 'center' }}>{t.name}<span className="cm-max">{fmtPct(Number(t.percentage))}</span></th>;
                        })}
                        <th rowSpan={2} style={{ verticalAlign: 'bottom', textAlign: 'center' }}>Total<span className="cm-max">{totalMax}</span></th>
                      </tr>
                      <tr>
                        {topLevel.filter(t => components.some(c => c.parent_id === t.id)).flatMap(t =>
                          components.filter(c => c.parent_id === t.id).map(ch => (
                            <th key={ch.id} className={orderedLeaves.find(l => l.id === ch.id)?.groupStart ? 'cm-group-start' : ''} style={{ textAlign: 'center', fontWeight: 400 }}>{ch.name}<span className="cm-max">{fmtPct(Number(ch.percentage))}</span></th>
                          ))
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {loadingRoster ? <TableSkeletonRows columns={1 + orderedLeaves.length + 1} /> : roster.map(r => {
                        const locked = r.status === 'locked' || r.status === 'released';
                        const lockTitle = r.status === 'released'
                          ? 'Locked — released to the student, can no longer be edited'
                          : 'Locked by admin — ask admin to unlock before editing';
                        return (
                        <tr key={r.student_no} className={locked ? 'sms-row-muted' : undefined}>
                          <td>
                            <div className="cm-stu-cell">
                              <span className="cm-avatar">{initials(r.name)}</span>
                              <span>
                                <div>{r.name}{locked && <i className="bi bi-lock-fill" title={lockTitle} style={{ marginLeft: 6, fontSize: 12, color: 'var(--sms-txt3)' }} />}</div>
                                <div className="cm-stu-id">{r.student_no}</div>
                              </span>
                            </div>
                          </td>
                          {orderedLeaves.map(l => {
                            const key = cellKey(r.student_no, l.id);
                            const isOver = !!overLimit[key];
                            const isEmpty = r.scores[l.id] === '' || r.scores[l.id] == null;
                            return (
                              <td key={l.id} className={l.groupStart ? 'cm-group-start' : ''} style={{ textAlign: 'center' }}>
                                <input
                                  ref={el => { inputRefs.current[key] = el; }}
                                  type="number" min="0" max={l.percentage}
                                  disabled={locked}
                                  title={locked ? lockTitle : undefined}
                                  className={`sms-form-control cm-mark-input${isEmpty ? ' empty' : ''}`}
                                  style={{
                                    ...(isOver ? {
                                      borderColor: 'var(--sms-btn-outline-danger-txt, #dc2626)',
                                      boxShadow: '0 0 0 2px rgba(220,38,38,0.25)',
                                      color: 'var(--sms-btn-outline-danger-txt, #dc2626)',
                                    } : {}),
                                  }}
                                  value={r.scores[l.id]}
                                  onChange={e => setScore(r.student_no, l.id, e.target.value)}
                                />
                              </td>
                            );
                          })}
                          <td className="num" style={{ fontWeight: 600, color: rowOverTotal(r) ? 'var(--sms-btn-outline-danger-txt, #dc2626)' : undefined }}>{Math.round(rowTotal(r) * 10) / 10} / {totalMax}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {anyOverLimit && (
                  <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt, #dc2626)', marginTop: 8 }}>
                    <i className="bi bi-exclamation-triangle" /> One or more marks exceed their component's maximum — fix the highlighted field(s) before saving.
                  </div>
                )}
                {anyOverTotal && (
                  <div style={{ fontSize: 12, color: 'var(--sms-btn-outline-danger-txt, #dc2626)', marginTop: 8 }}>
                    <i className="bi bi-exclamation-triangle" /> One or more students' total exceeds {totalMax} — fix the highlighted total(s) before saving.
                  </div>
                )}
                <div className="d-flex justify-content-end mt-3">
                  <button type="button" className="sms-btn sms-btn-primary" disabled={saving || anyOverLimit || anyOverTotal} onClick={handleSave}>
                    {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save marks</>}
                  </button>
                </div>
              </>
            )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
